import { createTheme, Theme, ThemeOptions } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import typography from "./Typography";
import shadows from "./Shadows";
import getThemedComponents from "./Override";
import palette from "./palette";

/**
 * App shell dimensions, per the design system's §08: a 264px sidebar rail and a
 * 64px top bar. Both were narrower/shorter (224/50) before the Playful flip —
 * the sidebar because its rows were bare text rather than the 40px pills it now
 * carries, and the top bar because it held a denser, smaller-type header.
 *
 * `SidebarWidth` is ONE OF THREE representations of the same width. The sidebar
 * sets its own width from this constant, while the main content clears it with
 * `tokens.space.px224` and `tokens.space.sidebarOpenPaddingLeft` — so all three
 * must change together or the content renders underneath the rail. (That is
 * exactly what happened when this was first raised to 264 on its own.)
 */
const SidebarWidth = 264;
const TopbarHeight = 64;

const baseTheme: ThemeOptions = {
  palette,
  /**
   * Professional's composed radii — tighter than Playful's. `borderRadius` is
   * the scalar MUI multiplies a bare numeric `borderRadius` by (in `sx`, in
   * `unstable_sx`, and in its own unstyled component defaults), so it carries
   * the card value; the four named roles are read directly.
   *
   * A bare number inside `theme.unstable_sx` is MULTIPLIED by `borderRadius`.
   * Always interpolate: `` `${theme.shape.control}px` ``.
   */
  shape: {
    borderRadius: 8,
    /** Inputs, menu rows, alert panels, small controls. */
    control: 6,
    /** Cards, filled alerts, panels. Same value as `borderRadius`. */
    card: 8,
    /** Dialogs and menu papers. */
    dialog: 10,
    /** Buttons, chips, icon buttons, progress bars. */
    pill: 999,
  },
  /**
   * 8px base, unchanged — and deliberately NOT the 12/20/30/42/60 ramp the
   * design system's "Shape & Spacing" card appears to show.
   *
   * Those five numbers are the pixel HEIGHTS of the illustrative bars in that
   * card's diagram (`height:{{ s.h }}` on a 16px-wide swatch), labelled 1×–6×.
   * They are not the token values: the doc states an 8px base in three separate
   * places — `DESIGN_SYSTEM.md`'s "Spacing: 8px base", its "apply the spacing
   * grid — 8px base unit", and the card's own "Spacing — 8px" title — and its
   * real mockup padding (16/18/20/22/24/26px) follows neither 12/20/30/42/60
   * nor a strict 8 grid. Treating decorative bar heights as the scale would
   * re-space every `mt`/`p`/`gap` multiplier in the app off a diagram.
   */
  spacing: 8,
  /**
   * Both branches use `TopbarHeight`. The narrow one used to hardcode 50px,
   * which matched only because `TopbarHeight` was also 50 — so raising the
   * constant to the design system's 64 desynced them, and `MainLayout`'s
   * `paddingBottom: TopbarHeight` (which clears the mobile bottom bar, the same
   * Toolbar repositioned) started reserving 14px more than the bar occupies.
   * There is one top bar height, so it is named once.
   */
  mixins: {
    toolbar: {
      "@media(min-width:1280px)": {
        minHeight: TopbarHeight,
        padding: "0 30px",
      },
      "@media(max-width:1280px)": {
        minHeight: TopbarHeight,
      },
    },
  },
  /**
   * A breakpoint is the range of predetermined screen sizes that have specific
   * layout requirements.
   *
   *  - xs (extra-small): 0px or larger
   *  - sm (small): 475px or larger
   *  - md (medium): 725px or larger
   *  - lg (large): 1050px or larger
   *  - xl (extra-large): 1250px or larger
   */
  breakpoints: {
    values: { xs: 0, sm: 475, md: 725, lg: 1050, xl: 1250 },
  },
  typography,
  shadows,
};

const brandTheme = createTheme(baseTheme);

/**
 * Built once at module load. Component overrides need the assembled theme to
 * read spacing and palette out of, which is why this is two steps.
 */
const theme: Theme = deepmerge(brandTheme, getThemedComponents(brandTheme));

export default theme;
export { TopbarHeight, SidebarWidth, baseTheme };
